import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { translations, Language } from "@/lib/i18n/translations";

type DeepStringify<T> = {
  [K in keyof T]: T[K] extends object ? DeepStringify<T[K]> : string;
};

export type TranslationStrings = DeepStringify<typeof translations.he>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationStrings;
  isRTL: boolean;
  locale: string;
  currencySymbol: string;
  /** Convert a route path to its language-specific URL. English → returns
   *  path unchanged; Hebrew → prefixes `/he`. Used by the navbar's language
   *  toggle to navigate to the correct localized URL and by sitemap/prerender
   *  to enumerate every (route × language) combination. */
  localizePath: (path: string, lang?: Language) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "flowly-language";

/** Prefix for the Hebrew-prerendered URL tree. `/he/pricing` serves Hebrew
 *  static HTML; `/pricing` serves English. Crawlers and AI bots (which don't
 *  execute JS) see distinct content per URL, fixing the Hebrew SEO gap that
 *  client-side language detection alone cannot solve. */
const HE_PREFIX = "/he";

/** True if a pathname is in the Hebrew URL tree. */
function isHebrewPath(pathname: string): boolean {
  return pathname === HE_PREFIX || pathname.startsWith(`${HE_PREFIX}/`);
}

/** Add or remove the `/he` prefix on a path to produce its other-language
 *  counterpart. Idempotent — safe to call regardless of input language. */
function withLangPrefix(path: string, lang: Language): string {
  const stripped = isHebrewPath(path)
    ? path.slice(HE_PREFIX.length) || "/"
    : path;
  if (lang === "he") {
    return stripped === "/" ? HE_PREFIX : `${HE_PREFIX}${stripped}`;
  }
  return stripped;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      // The URL is the strongest source of truth — `/he/*` is the Hebrew
      // prerendered tree, so a visit to those URLs must render Hebrew
      // regardless of localStorage / browser language / timezone. Crawlers
      // landing directly on `/he/pricing` get Hebrew on first paint with
      // no JS-time flicker.
      if (isHebrewPath(window.location.pathname)) return "he";

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === "he" || stored === "en")) {
        return stored as Language;
      }
      // Geo-default for first-time visitors: visitors from Israel see Hebrew,
      // everyone else sees English. Browser timezone is a fast, offline,
      // permission-free proxy for "is this person in Israel" — no IP service
      // round-trip, no Cloudflare header dependency.
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === "Asia/Jerusalem") return "he";
      } catch {
        // Older browsers without resolvedOptions — fall through to browser lang.
      }
      const browserLangs = navigator.languages ?? [navigator.language];
      if (browserLangs.some((lang) => lang.startsWith("he"))) {
        return "he";
      }
    }
    return "en";
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    // Also persist to a cookie so the edge middleware (which can't read
    // localStorage) honors this explicit choice on the next full page load.
    document.cookie = `${STORAGE_KEY}=${lang}; path=/; max-age=31536000; samesite=lax`;
  }, []);

  const localizePath = useCallback(
    (path: string, lang: Language = language) => withLangPrefix(path, lang),
    [language],
  );

  const t = translations[language] as TranslationStrings;
  const isRTL = language === "he";
  const locale = language === "he" ? "he-IL" : "en-US";
  const currencySymbol = language === "he" ? "₪" : "$";

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language, isRTL]);

  // One-time backfill: users who chose a language before the cookie existed
  // have it only in localStorage. Mirror it into the cookie so the edge
  // middleware honors their explicit choice from the next visit on.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && !document.cookie.includes(`${STORAGE_KEY}=`)) {
      document.cookie = `${STORAGE_KEY}=${stored}; path=/; max-age=31536000; samesite=lax`;
    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      isRTL,
      locale,
      currencySymbol,
      localizePath,
    }),
    [language, setLanguage, t, isRTL, locale, currencySymbol, localizePath],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/** Re-exported so prerender / sitemap / RouteSEO can introspect a pathname
 *  without owning a LanguageProvider. */
export { isHebrewPath, withLangPrefix, HE_PREFIX };

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
